import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

const ZendeskWidget = () => {
  // Track current location to detect navigation
  const location = useLocation();
  const [isWidgetLoaded, setIsWidgetLoaded] = useState(false);

  // Get widget URL from environment variable
  const widgetUrl = import.meta.env.REACT_APP_ZENDESK_WIDGET;

  // Extract widget key from URL (format: https://static.zdassets.com/ekr/snippet.js?key=XXX)
  let widgetKey = null;
  if (widgetUrl) {
    try {
      widgetKey = new URL(widgetUrl).searchParams.get('key');
    } catch (e) {
      /* empty */
    }
  }

  // Zendesk custom field ID for tracking code (from Admin > Objects and rules > Ticket fields)
  const trackingCodeFieldId = import.meta.env.REACT_APP_ZENDESK_TRACKING_CODE_FIELD_ID || '1500010277602';

  // Extract tracking code from URL path
  const getTrackingCodeFromUrl = () => {
    // Parse tracking code from URL path: /:tracking_code or /noredirect/:tracking_code
    const path = window.location.pathname;
    const match = path.match(/^\/(?:noredirect\/)?([A-Za-z0-9\-_]+)(?:\/|$)/);
    if (match?.[1] && !['error', 'list', 'showpod'].includes(match[1])) {
      return match[1];
    }
    return null;
  };

  // Load Zendesk widget script (only once)
  useEffect(() => {
    if (!widgetUrl) {
      return;
    }

    // Check if script already exists
    if (document.getElementById('ze-snippet')) {
      setIsWidgetLoaded(true);
      return;
    }

    // Load Zendesk script
    const script = document.createElement('script');
    script.id = 'ze-snippet';
    script.src = `https://static.zdassets.com/ekr/snippet.js?key=${widgetKey}`;
    script.async = true;

    script.onload = () => {
      setIsWidgetLoaded(true);
    };

    document.head.appendChild(script);

    return () => {
      const scriptToRemove = document.getElementById('ze-snippet');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
      if (window.zE) {
        window.zE('messenger', 'close');
      }
    };
  }, [widgetKey]);

  // Update tracking code when URL changes
  useEffect(() => {
    if (!isWidgetLoaded || !window.zE) {
      return;
    }

    const trackingCode = getTrackingCodeFromUrl();

    // Update conversation fields
    if (trackingCode) {
      const conversationFields = [{ id: trackingCodeFieldId, value: trackingCode }];

      // Set fields immediately
      window.zE('messenger:set', 'conversationFields', conversationFields);

      // Also set when widget opens
      window.zE('messenger:on', 'open', () => {
        window.zE('messenger:set', 'conversationFields', conversationFields);
      });
    }
  }, [location.pathname, isWidgetLoaded, trackingCodeFieldId]);

  return null;
};

export default ZendeskWidget;
